import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import TicketDetails from "../components/TicketDetails";
import Layout from "../components/Layout/Layout";
import TicketForm from "../components/TicketForm";
import { PlusIcon } from "@heroicons/react/24/solid"; // or /outline for outline version
import { XMarkIcon } from "@heroicons/react/24/solid";
import Select from "react-select";
import Dashboard from "./Dashboard";

import api from "../utils/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
} from "chart.js";
ChartJS.register(
  ArcElement,
  BarElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale
);
import TicketDashboardSummary from "../components/TicketDashboardSummary";
import TicketTable from "../components/TicketTable";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import {
  getStatusColor,
  getStatusTextColor,
  getStatusIcon,
} from "../utils/statusStyles";
import { useUser } from "../contexts/UserContext";
import { useClient } from "../contexts/ClientContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { format } from "date-fns";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ArrowPathIcon } from "@heroicons/react/24/solid";

export default function TicketListPage() {
  const { user } = useUser();
  const { selectedClientId, setSelectedClientId } = useClient();
  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-lg text-gray-600">
        Loading user information...
      </div>
    );
  }
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [selectedTicketChildId, setSelectedTicketChildId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem("ticketRowsPerPage");
    return saved ? parseInt(saved, 10) : 10;
  });
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [minTicketDate, setMinTicketDate] = useState(null);
  const [maxTicketDate, setMaxTicketDate] = useState(null);
  const safeMinDate = minTicketDate ?? new Date(new Date().getFullYear() - 1, 0, 1);
  const safeMaxDate = maxTicketDate ?? new Date();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [userStatus, setUserStatus] = useState(null);
  const searchInputRef = useRef(null);
  const [filterDate, setFilterDate] = useState("");
  const [priority, setPriority] = useState([]);
  const [module, setModule] = useState([]);
  const [showBadges, setShowBadges] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ticketIdFromUrl = searchParams.get("tid");

  const handleTicketClick = useCallback((ticket) => {
    setSelectedTicketId(ticket?.id);
    setSelectedTicketChildId(ticket?.child_id);
  }, []);

  const closeDetails = useCallback(() => {
    setSelectedTicketId(null);
    setSelectedTicketChildId(null);
  }, []);

  useEffect(() => {
    document.title = "Enova E - Ticketing";
  }, []);

  useEffect(() => {
    localStorage.setItem("ticketRowsPerPage", String(rowsPerPage));
  }, [rowsPerPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (searchInputRef.current && user?.role_id == 2) {
      searchInputRef.current.focus();
    }
  }, []);

  // Load full dataset once; all search/filter/summaries are client-side.
  const loadTickets = useCallback(
    async (signal) => {
      setIsLoading(true);
      try {
        const res = await api.get("/ticket", {
          params: { child_id: user?.child_id ?? "" },
          ...(signal && { signal }),
        });
        const data = res.data;
        const raw = (data?.data != null)
          ? (Array.isArray(data.data) ? data.data : [data.data])
          : [];
        if (data && data.status === true) {
          const uid = user?.id ?? user?.user_id;
          const mapped = raw.map((t) => {
            return {
            id: t.id,
            collegeName: t.college_name,
            client: t.client_name,
            client_key: t.client_key,
            module: t.module_name,
            title: t.title,
            description: t.description,
            priority: t.priority,
            status: t.status,
            status_id: t.status_id,
            assign_user_id: t.assign_user_id,
            created_by_user_id: t.created_by_user_id,
            assignedTo: t.assigned_to,
            AssignedTo_designation_id: t.AssignedTo_designation_id,
            ReportedBy_designation_id: t.ReportedBy_designation_id,
            created: t.created_date,
            updated: t.updated_date,
            involves_user: t.involves_user,
            comment_count: t.comment_count,
            child_id: t.child_id,
            issue_type: t.issue_type,
            customer_name: t.customer_name,
            priority_id: t.priority_id,
            module_id: t.module,
          };
          });
          setTickets(mapped);
          const dates = mapped.map((t) => t.created).filter(Boolean);
          if (dates.length) {
            setMinTicketDate(new Date(Math.min(...dates.map((d) => new Date(d)).map(Number))));
            setMaxTicketDate(new Date(Math.max(...dates.map((d) => new Date(d)).map(Number))));
          } else {
            setMinTicketDate(null);
            setMaxTicketDate(null);
          }
        } else {
          setTickets([]);
          setMinTicketDate(null);
          setMaxTicketDate(null);
        }
      } catch (err) {
        if (err?.code === "ERR_CANCELED" || err?.name === "AbortError") return;
        console.error("Fetch failed:", err);
      } finally {
        setIsLoading(false);
      }
    },
    [user?.child_id, user?.id ?? user?.user_id]
  );

  useEffect(() => {
    const controller = new AbortController();
    loadTickets(controller.signal);
    return () => controller.abort();
  }, [loadTickets]);

  const userId = user?.id ?? user?.user_id ?? null;
  const roleId = Number(user?.role_id) || 0;

  const filteredTickets = useMemo(() => {
    let list = tickets;
    const q = (debouncedSearch || "").toLowerCase().trim();
    if (q) {
      list = list.filter(
        (t) =>
          String(t.title || "").toLowerCase().includes(q) ||
          String(t.id || "").toLowerCase().includes(q) ||
          String(t.issue_type || "").toLowerCase().includes(q) ||
          String(t.collegeName || "").toLowerCase().includes(q) ||
          String(t.module || "").toLowerCase().includes(q) ||
          String(t.priority || "").toLowerCase().includes(q) ||
          String(t.status || "").toLowerCase().includes(q) ||
          String(t.assignedTo || "").toLowerCase().includes(q) ||
          String(t.client || "").toLowerCase().includes(q) ||
          String(t.created || "").toLowerCase().includes(q) ||
          String(t.updated || "").toLowerCase().includes(q)
      );
    }
    if (selectedStatus != null && selectedStatus !== "") {
      list = list.filter((t) => String(t.status_id) === String(selectedStatus));
    }
    if (userStatus === "waiting") {
      list = list.filter(
        (t) =>
          t.status_id != null &&
          Number(t.status_id) !== 5 &&
          (
            (String(t.assign_user_id) === String(userId) && Number(t.status_id) !== 3) ||
            (Number(t.status_id) === 3 && roleId === 2)
          )
      );
    }
    
    else if (userStatus === "open") {
      if (roleId === 2) {
        // Admin: all open tickets
        list = list.filter(
          (t) => [1, 4, 6].includes(Number(t.status_id))
        );
      } else {
        // CA/User: ONLY tickets created by me
        list = list.filter(
          (t) =>
            [1, 4, 6].includes(Number(t.status_id)) &&
            String(t.created_by_user_id) === String(userId)
        );
      }
    }
    
    
    else if (userStatus === "mytickets") {
      list = list.filter(
        (t) =>
          Number(t.involves_user) === 1 ||
          String(t.assign_user_id) === String(userId) ||
          String(t.created_by_user_id) === String(userId)
      );
    }
    
    
    if (filterDate) {
      const fd = format(new Date(filterDate), "yyyy-MM-dd");
      list = list.filter(
        (t) => t.created && format(new Date(t.created), "yyyy-MM-dd") === fd
      );
    }
    if (selectedClientId) {
      list = list.filter((t) => t.client_key === selectedClientId);
    }
    if (Array.isArray(priority) && priority.length) {
      const set = new Set(priority.map(String));
      list = list.filter((t) => set.has(String(t.priority_id)));
    }
    if (Array.isArray(module) && module.length) {
      const set = new Set(module.map(String));
      list = list.filter((t) => set.has(String(t.module_id)));
    }
    if (fromDate) {
      const fd = format(new Date(fromDate), "yyyy-MM-dd");
      list = list.filter(
        (t) => t.created && format(new Date(t.created), "yyyy-MM-dd") >= fd
      );
    }
    if (toDate) {
      const td = format(new Date(toDate), "yyyy-MM-dd");
      list = list.filter(
        (t) => t.created && format(new Date(t.created), "yyyy-MM-dd") <= td
      );
    }
    return list;
  }, [
    tickets,
    debouncedSearch,
    selectedStatus,
    userStatus,
    filterDate,
    selectedClientId,
    priority,
    module,
    fromDate,
    toDate,
    userId,
    roleId,
  ]);


  // ✅ College-wise base dataset (VERY IMPORTANT)
const collegeTickets = useMemo(() => {
  if (!selectedClientId) return tickets;

  return tickets.filter(
    (t) => String(t.client_key) === String(selectedClientId)
  );
}, [tickets, selectedClientId]);


  const statusSummary = useMemo(() => {
    const byId = {};
    collegeTickets.forEach((t) => {
      const id = Number(t.status_id);
      if (!byId[id]) {
        byId[id] = { count: 0, label: t.status || "" };
      }
      byId[id].count += 1;
    });
  
    return Object.entries(byId).map(([id, o]) => ({
      status_id: Number(id),
      label: o.label,
      count: o.count,
      color: getStatusColor(Number(id)),
      text: getStatusTextColor(Number(id)),
      icon: getStatusIcon(Number(id)),
    }));
  }, [collegeTickets]);
  
  const userStatusCounts = useMemo(() => {
    let waiting = 0;
    let open = 0;
    let mytickets = 0;
  
    collegeTickets.forEach((t) => {
      const statusId = Number(t.status_id);
  
      const isAssignedToMe =
        String(t.assign_user_id) === String(userId);
  
      const isCreatedByMe =
        String(t.created_by_user_id) === String(userId);
  
      const isInvolved =
        Number(t.involves_user) === 1;
  
      // My Tickets (all roles)
      if (isInvolved) mytickets++;
  
      // Waiting on Me — MUST MATCH TABLE LOGIC
      if (
        statusId != null &&
        statusId !== 5 &&
        (
          (String(t.assign_user_id) === String(userId) && statusId !== 3) ||
          (statusId === 3 && roleId === 2)
        )
      ) {
        waiting++;
      }

      // Open Tickets (non-client only)
      if (
        roleId !== 3 &&
        isCreatedByMe &&
        [1, 4, 6].includes(statusId)
      ) {
        open++;
      }
    });
  
    return { waiting, open, mytickets };
  }, [collegeTickets, userId, roleId]);
  
  
  

  const collegeName = useMemo(() => {
    if (!selectedClientId || !tickets.length) return " - Overall";
    const first = tickets.find((t) => t.client_key === selectedClientId);
    if (!first) return " - Overall";
    return " - " + String((first.client || "") + " (" + (first.collegeName || "") + ")").toUpperCase();
  }, [tickets, selectedClientId]);

  const priorityOptions = useMemo(() => {
    const map = new Map();
    tickets.forEach((t) => {
      const v = String(t.priority_id ?? "");
      if (v && !map.has(v)) map.set(v, { value: v, label: t.priority || v });
    });
    return Array.from(map.values()).filter((o) => o.value);
  }, [tickets]);

  const moduleOptions = useMemo(() => {
    const map = new Map();
    tickets.forEach((t) => {
      const v = String(t.module_id ?? "");
      if (v && !map.has(v)) map.set(v, { value: v, label: t.module || v });
    });
    return Array.from(map.values()).filter((o) => o.value);
  }, [tickets]);

  const statusCounts = useMemo(
    () =>
      filteredTickets.reduce(
        (acc, ticket) => {
          acc.total += 1;
          const statusMap = {
            1: "open",
            3: "pending",
            4: "open",
            5: "resolved",
            6: "canceled",
          };
          const key = statusMap[ticket.status_id] || "unknown";
          if (acc[key] !== undefined) acc[key] += 1;
          return acc;
        },
        { total: 0, open: 0, resolved: 0, canceled: 0 }
      ),
    [filteredTickets]
  );

  const handleFilterClick = useCallback((key) => {
    setSearch("");
    setModule("");
    setPriority("");
    setFromDate(null);
    setToDate(null);
    setSelectedStatus(null);
    setUserStatus((prev) => (prev === key ? null : key));
  }, []);

  const applyFilter = useCallback(({ status = null, user = null }) => {
    setSearch("");
    setModule("");
    setPriority("");
    setFromDate(null);
    setToDate(null);
    setSelectedStatus(status);
    setUserStatus(user);
  }, []);

  const statusOptions = [
    { value: "1", label: "New" },
    { value: "4", label: "IN process" },
    { value: "3", label: "Pending verification & closure" },
    { value: "5", label: "Closed" },
    { value: "6", label: "Reopen" },
  ];

  useEffect(() => {
    if (tickets.length > 0 && ticketIdFromUrl) {
      const matched = tickets.find(
        (t) => String(t.id).trim() === String(ticketIdFromUrl).trim()
      );

      if (matched) {
        handleTicketClick(matched);

        // Remove the tid param from URL
        navigate("/tickets", { replace: true });
      }
    }
  }, [tickets, ticketIdFromUrl]);

  return (
    <Layout noSidebar={Number(user?.role_id) !== 2 ? false : true}>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="p-2 bg-neutral-light min-h-full relative ">
        <div className="bg-white rounded-xl shadow px-3 py-2">
          <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            {/* Title */}
            <div className="flex flex-col sm:flex-col lg:flex-row lg:items-center gap-2 flex-1">
              <div className="flex items-center justify-between">
                <h2 className="text-base sm:text-md md:text-lg xl:text-xl font-medium  truncate">
                  Ticket Dashboard {collegeName}
                </h2>

                <button
                  onClick={() => setShowBadges(!showBadges)}
                  className="sm:hidden text-sm text-primary underline flex items-center gap-1"
                >
                  {showBadges ? <FiChevronUp /> : <FiChevronDown />}
                </button>
              </div>

              {/* Status Badges */}
            </div>
          </div>

          <TicketDashboardSummary
            statusSummary={statusSummary}
            statusCounts={statusCounts}
            onStatusClick={(statusId) => applyFilter({ status: statusId })}
            selectedStatusId={selectedStatus}
            userStatus={userStatus}
          />

          {/* Buttons */}
          <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
            <div
              className={`flex flex-wrap gap-2  ${showBadges ? "" : "hidden"} `}
            >
              {[
                {
                  label: "Waiting on Me",
                  count: userStatusCounts.waiting,
                  color: " text-yellow-800",
                  key: "waiting",
                  note: "Tickets assigned to you that require your action.",
                },
                {
                  label: "Open Tickets",
                  count: userStatusCounts.open,
                  color: " text-orange-800",
                  key: "open",
                  note: "Tickets that are new, in progress, or reopened.",
                },
                {
                  label: "My Tickets",
                  count: userStatusCounts.mytickets,
                  color: " text-blue-800",
                  key: "mytickets",
                  note: "Tickets you created, updated, or are assigned to.",
                },
              ].map(({ label, count, color, key, note }) => {
                const isActive = userStatus === key;
                return (
                  <div key={key} className="relative group">
                    <button
                      onClick={() => handleFilterClick(key)}
                      className={`px-2 py-1.5 rounded-full font-medium text-textPrimary text-sm flex items-center gap-2 transition border whitespace-nowrap justify-center md:justify-start ${color}
          ${isActive ? "bg-main text-white" : "hover:shadow-md"}`}
                    >
                      <span>{label}</span>
                      <span className="bg-white text-black px-2 py-0.5 rounded-full text-xs font-semibold">
                        {count}
                      </span>
                    </button>

                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 hidden group-hover:block w-48 text-sm text-white bg-gray-900 rounded-lg px-3 py-2 shadow-lg">
                      {note}
                      <div className="absolute left-4 top-full w-2 h-2 bg-gray-900 rotate-45"></div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-2 justify-end w-full md:w-auto">
              {['1', '2', '3'].includes(user?.role_id) && (
              <button
                className="bg-red-700 hover:bg-red-800 text-sm text-white px-2 py-1 rounded-md transition font-medium whitespace-nowrap inline-flex items-center gap-2"
                onClick={() => setShowForm(true)}
              >
                <PlusIcon className="w-5 h-5 text-white-200 " />
                Create Ticket
              </button>
              )}

              <button
                onClick={() => loadTickets()}
                className=" text-sm px-4 py-1 rounded-md transition font-medium whitespace-nowrap inline-flex items-center gap-2 border  "
              >
                <ArrowPathIcon className="w-4 h-4" />
                <span className="hidden sm:inline ml-1">Refresh</span>
              </button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 ">
            {/* Search + Date Filter */}
            <div className="flex flex-col md:flex-row flex-wrap items-center justify-between gap-3 w-full">
              <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                <div className="relative w-full md:w-[120px]">
                  {/* <label className="text-sm block mb-1 font-medium">Rows per page:</label> */}
                  <select
                    value={rowsPerPage || 10}
                    onChange={(e) => setRowsPerPage(Number(e.target.value))}
                    className="border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 w-full bg-white"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end">
                 <div className="relative w-1/2 md:w-[150px]">
                  <DatePicker
                    popperProps={{
                      strategy: "fixed",
                    }}
                    popperClassName="z-50"
                    selected={fromDate}
                    onChange={(date) => {
                      setFromDate(date);
                    }}
                    placeholderText="From date →"
                    dateFormat="dd/MM/yyyy"
                    minDate={safeMinDate}
                    maxDate={toDate || safeMaxDate}
                    isClearable
                    renderCustomHeader={({
                      date,
                      changeYear,
                      changeMonth,
                      decreaseMonth,
                      increaseMonth,
                      prevMonthButtonDisabled,
                      nextMonthButtonDisabled,
                    }) => (
                      <div className="flex items-center justify-between px-2 py-1">
                        <button
                          onClick={decreaseMonth}
                          disabled={prevMonthButtonDisabled}
                          className="px-2 text-gray-600 hover:text-black"
                        >
                          ‹
                        </button>

                        <div className="flex gap-2">
                          {/* Month dropdown */}
                          <select
                            value={date.getMonth()}
                            onChange={({ target: { value } }) =>
                              changeMonth(Number(value))
                            }
                            className="border rounded px-2 py-0.5 text-sm font-bold appearance-none bg-white"
                            style={{ backgroundImage: "none" }}
                          >
                            {(() => {
                              const year = date.getFullYear();
                              const startMonth =
                                year === safeMinDate.getFullYear()
                                  ? safeMinDate.getMonth()
                                  : 0;
                              const endMonth =
                                year === safeMaxDate.getFullYear()
                                  ? safeMaxDate.getMonth()
                                  : 11;

                              return Array.from(
                                { length: endMonth - startMonth + 1 },
                                (_, i) => startMonth + i
                              ).map((month) => (
                                <option key={month} value={month}>
                                  {
                                    [
                                      "January",
                                      "February",
                                      "March",
                                      "April",
                                      "May",
                                      "June",
                                      "July",
                                      "August",
                                      "September",
                                      "October",
                                      "November",
                                      "December",
                                    ][month]
                                  }
                                </option>
                              ));
                            })()}
                          </select>

                          {/* Year dropdown */}
                          <select
                            value={date.getFullYear()}
                            onChange={({ target: { value } }) =>
                              changeYear(Number(value))
                            }
                            className="border rounded px-1 py-0.5 text-sm font-bold appearance-none bg-white"
                            style={{
                              backgroundImage: "none",
                            }}
                          >
                            {Array.from(
                              {
                                length:
                                  safeMaxDate.getFullYear() -
                                  safeMinDate.getFullYear() +
                                  1,
                              },
                              (_, i) => safeMinDate.getFullYear() + i
                            ).map((year) => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                        </div>

                        <button
                          onClick={increaseMonth}
                          disabled={nextMonthButtonDisabled}
                          className="px-2 text-gray-600 hover:text-black"
                        >
                          ›
                        </button>
                      </div>
                    )}
                    className="pl-4 py-2 border border-gray-200 rounded w-full focus:outline-none focus:ring-1 text-sm"
                  />
                </div>

                <div className="relative w-1/2 md:w-[150px]">
                  <DatePicker
                    popperProps={{
                      strategy: "fixed",
                    }}
                    popperClassName="z-50"
                    selected={toDate}
                    onChange={(date) => {
                      setToDate(date);
                    }}
                    placeholderText="← To date"
                    dateFormat="dd/MM/yyyy"
                    minDate={fromDate || safeMinDate}
                    maxDate={safeMaxDate}
                    isClearable
                    renderCustomHeader={({
                      date,
                      changeYear,
                      changeMonth,
                      decreaseMonth,
                      increaseMonth,
                      prevMonthButtonDisabled,
                      nextMonthButtonDisabled,
                    }) => (
                      <div className="flex items-center justify-between px-2 py-1">
                        <button
                          onClick={decreaseMonth}
                          disabled={prevMonthButtonDisabled}
                          className="px-2 text-gray-600 hover:text-black"
                        >
                          ‹
                        </button>

                        <div className="flex gap-2">
                          <select
                            value={date.getMonth()}
                            onChange={({ target: { value } }) =>
                              changeMonth(Number(value))
                            }
                            className="border rounded px-2 py-0.5 text-sm font-bold appearance-none bg-white"
                            style={{ backgroundImage: "none" }}
                          >
                            {(() => {
                              const year = date.getFullYear();
                              const startMonth =
                                year === safeMinDate.getFullYear()
                                  ? safeMinDate.getMonth()
                                  : 0;
                              const endMonth =
                                year === safeMaxDate.getFullYear()
                                  ? safeMaxDate.getMonth()
                                  : 11;

                              return Array.from(
                                { length: endMonth - startMonth + 1 },
                                (_, i) => startMonth + i
                              ).map((month) => (
                                <option key={month} value={month}>
                                  {
                                    [
                                      "January",
                                      "February",
                                      "March",
                                      "April",
                                      "May",
                                      "June",
                                      "July",
                                      "August",
                                      "September",
                                      "October",
                                      "November",
                                      "December",
                                    ][month]
                                  }
                                </option>
                              ));
                            })()}
                          </select>

                          <select
                            value={date.getFullYear()}
                            onChange={({ target: { value } }) =>
                              changeYear(Number(value))
                            }
                            className="border rounded px-1 py-0.5 text-sm font-bold appearance-none bg-white"
                            style={{
                              backgroundImage: "none",
                            }}
                          >
                            {Array.from(
                              {
                                length:
                                  safeMaxDate.getFullYear() -
                                  safeMinDate.getFullYear() +
                                  1,
                              },
                              (_, i) => safeMinDate.getFullYear() + i
                            ).map((year) => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                        </div>

                        <button
                          onClick={increaseMonth}
                          disabled={nextMonthButtonDisabled}
                          className="px-2 text-gray-600 hover:text-black"
                        >
                          ›
                        </button>
                      </div>
                    )}
                    className="pl-4 py-2 border border-gray-200 rounded w-full focus:outline-none focus:ring-1 text-sm"
                  />
                </div>

                <div className="relative w-1/2 md:w-[180px]">
                  <Select
                    isMulti
                    name="modules"
                    options={moduleOptions}
                    classNamePrefix="select"
                    className="w-full focus:outline-none focus:ring-1 text-sm"
                    value={moduleOptions.filter((o) =>
                      module.includes(o.value)
                    )}
                    onChange={(selected) =>
                      setModule(selected.map((o) => o.value))
                    }
                    placeholder="Select module..."
                  />
                </div>

                <div className="relative w-1/2 md:w-[180px]">
                  <Select
                    isMulti
                    name="priority"
                    options={priorityOptions}
                    classNamePrefix="select"
                    className="w-full focus:outline-none focus:ring-1 text-sm"
                    value={priorityOptions.filter((o) =>
                      priority.includes(o.value)
                    )}
                    onChange={(selected) =>
                      setPriority(selected.map((o) => String(o.value)))
                    }
                    placeholder="Select priority..."
                  />
                </div>

                <div className="relative w-full md:w-[160px] md:w-[180px]">
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="border border-gray-200 rounded px-4 py-1 pr-10 w-full focus:outline-none focus:ring-1  "
                    placeholder=" Search..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                    }}
                  />

                  {/* Clear Button */}
                  {search && (
                    <button
                      onClick={() => {
                        setSearch("");
                        searchInputRef.current?.focus();
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 
                           w-4 h-4 flex items-center justify-center 
                           text-midGray hover:text-white 
                           bg-[#216BA5] text-white
                           rounded-full text-xs shadow-sm transition"
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {isLoading ? (
            <table className="w-full border border-gray-300 rounded-lg overflow-hidden">
              <thead>
                <tr className="hover:bg-hover-row transition cursor-pointer font-sans font-medium">
                  <th className="px-1 py-3">Ticket ID</th>
                  {user?.role_id !== "2" && (
                    <th className="px-1 py-3">College Name</th>
                  )}
                  <th className="px-1 py-3">Module</th>
                  <th className="px-1 py-3">Title</th>
                  <th className="px-1 py-3">Assigned To</th>
                  <th className="px-1 py-3">Reported By</th>
                  <th className="px-1 py-3">Issue Type</th>
                  <th className="px-1 py-3">Created Date</th>
                  <th className="px-1 py-3">Last Updated</th>
                  <th className="px-1 py-3">Comments</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={idx} className="border-t border-gray-200">
                    <td className="px-4 py-3">
                      <div className="h-4 w-3/4 bg-gray-300 animate-pulse rounded" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 w-4/5 bg-gray-300 animate-pulse rounded" />
                    </td>
                    {user?.role_id !== "2" && (
                      <td className="px-4 py-3">
                        <div className="h-4 w-3/4 bg-gray-300 animate-pulse rounded" />
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="h-4 w-2/3 bg-gray-300 animate-pulse rounded" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 w-full bg-gray-300 animate-pulse rounded" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 w-1/2 bg-gray-300 animate-pulse rounded" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 w-2/3 bg-gray-300 animate-pulse rounded" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 w-1/2 bg-gray-300 animate-pulse rounded" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 w-3/4 bg-gray-300 animate-pulse rounded" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 w-2/3 bg-gray-300 animate-pulse rounded" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <>
              <TicketTable
                tickets={filteredTickets}
                onTicketClick={handleTicketClick}
                isLoading={isLoading}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(newRows) => setRowsPerPage(newRows)}
              />
            </>
          )}

          {selectedTicketId && (
            <TicketDetails
              ticketId={selectedTicketId}
              onClose={closeDetails}
              priorityOptions={priorityOptions}
              statusOptions={statusOptions}
              userInfo={user}
              selectedClientId={selectedClientId}
              selectedTicketChildId={selectedTicketChildId}
              onSuccess={() => {
                setShowForm(false);
                toast.success("Ticket updated successfully");
                loadTickets();
              }}
            />
          )}
        </div>

        {showForm && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm px-4"
            onClick={() => setShowForm(false)} // click outside closes
          >
            <div
              className="relative w-full max-w-4xl bg-white rounded-[20px] shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()} // stop closing when clicking inside
            >
              {/* Close Button */}
              <button
                onClick={() => setShowForm(false)}
                className="absolute top-5 right-5 w-6 h-6 bg-black text-white rounded-full flex items-center justify-center hover:bg-gray-200 hover:text-black transition"
                aria-label="Close"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>

              {/* Header */}
              <div className="p-6 border-b">
                <h2 className="text-2xl font-semibold text-center text-red-600">
                  Create New Ticket
                </h2>
                <p className="text-sm text-center text-gray-500 mt-1">
                  Fill in the details below to raise a support request.
                </p>
              </div>

              {/* Form */}
              <div className="px-6 py-4 space-y-6">
                <TicketForm
                  onSuccess={() => {
                    toast.success("Ticket created successfully");
                    loadTickets();
                    setShowForm(false);
                  }}
                  isAdmin={user?.role_id !== "2"}
                  priorityOptions={priorityOptions}
                  statusOptions={statusOptions}
                  userInfo={user}
                  selectedClientId={selectedClientId}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
